import React, { useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import composeRefs from '@seznam/compose-react-refs';
import { ExternalSystemId, ModelDTO, ModelGroupDTO } from '../../api/useModelGroupsQuery';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { TableCell, TableHeader, TableRow } from '../../../../../CommonComponents/OPL/Table/Table';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { TruncatedText } from '../../../../../CommonComponents/OPL/Text/TruncatedText';
import { columns } from './ModelGroup.constants';
import { ModelGroupEmpty } from './ModelGroup.empty';
import { showRiskToleranceWorkflowSelector } from '../../../../../CommonSelectors/CommonAreaSelector';
import { IconButton } from '../../../../../CommonComponents/OPL/IconButton/IconButton';
import { ModelRow, ModelsWrapper } from './ModelGroup.styles';
import { Tooltip } from '../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { DragHandle } from '../../../../../CommonComponents/OPL/DragHandle/DragHandle';
import { RiskScoreBadge } from '../RiskScoreBadge/RiskScoreBadge';
import { tooltipProps } from '../../Models.constants';
import { UpsertModelGroupActions } from '../UpsertModelGroup/useUpsertModelGroup';
import { OrionModelOptionDTO } from '../../api/useOrionModelOptionsQuery';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { Option } from './components/Option';
import { MenuList } from './components/Menu';
import { Select } from '../../../../../CommonComponents/OPL/Select/Select/Select';
import { isTempEntity } from '../../utils/isTempEntity';
import { makeNewModel } from '../../utils/makeNewModel';

interface Props {
  isLoading?: boolean;
  models: ModelDTO[];
  modelsActions?: UpsertModelGroupActions;
  modelGroup: Partial<ModelGroupDTO>;
  modelsOptions?: OrionModelOptionDTO[] | null;
  isReadonly?: boolean;
  setModelToEdit?(model: Partial<ModelDTO>): void;
  setModelToDelete?(model: ModelDTO): void;
  onModelSelection?(): void;
}

export const ModelGroup: React.FC<Props> = ({
  isReadonly,
  isLoading,
  models,
  modelsActions,
  modelsOptions,
  modelGroup,
  setModelToEdit,
  setModelToDelete,
  onModelSelection,
}) => {
  const showRiskToleranceWorkflow = useSelector(showRiskToleranceWorkflowSelector);

  const isOrion = modelGroup.externalSystem === ExternalSystemId.Orion;

  const isRiskalyze = modelGroup.externalSystem === ExternalSystemId.Riskalyze;

  const prevModalsLength = useRef<number>(models.length);

  const modelsWrapper = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    // We are adding a model -> scroll the models container all the way to the bottom
    if (models.length > prevModalsLength.current && modelsWrapper.current) {
      modelsWrapper.current.scrollTop = modelsWrapper.current.scrollHeight;
    }

    prevModalsLength.current = models.length;
  }, [models]);

  const onDragEnd = ({ source, destination }: DropResult) => {
    if (!destination) {
      return;
    }

    if (modelsActions) {
      modelsActions.moveModel(source.index, destination.index);
    }
  };

  const onDeleteModel = (model: ModelDTO, index: number) => {
    // It is an Orion Model Group but:
    // - The user has not selected a model yet, or
    // - The user is creating a new group, or
    // - The model is client side only.
    // No need to ask for permission to delete.
    if (
      isOrion &&
      (!model.orionModelId || !modelGroup.groupId || isTempEntity(model.id)) &&
      modelsActions
    ) {
      // We don't want to remove the last risk range.
      // Clear the selection instead.
      if (models.length === 1) {
        modelsActions.updateModelAt(0, makeNewModel());

        return;
      }

      modelsActions.removeModelAt(index);
    } else if (setModelToDelete) {
      setModelToDelete(model);
    }
  };

  return (
    <Box>
      <TableHeader borderStyle="solid" borderWidth="0 0 1px" borderColor="neutral.200">
        <TableRow>
          {!isReadonly && !modelGroup.externalSystem && <TableCell {...columns.dragHandle} />}

          {(showRiskToleranceWorkflow || isRiskalyze) && (
            <TableCell
              {...columns.riskBands}
              justifyContent={isReadonly || isRiskalyze ? 'center' : 'flex-start'}
            >
              <Text>{isRiskalyze ? 'Risk Score' : 'Risk Range'}</Text>
            </TableCell>
          )}

          <TableCell {...columns.model}>
            <Text mr="4">Model</Text>

            {isOrion && (
              <Tooltip content="Models are imported from Orion Trading products. Risk ranges should align with the risk score assigned to the model.">
                <Icon className="far fa-question-circle" fontSize="h6" color="neutral.500" />
              </Tooltip>
            )}
          </TableCell>

          {isOrion && (
            <TableCell {...columns.riskScore}>
              <Text>Risk Score</Text>
            </TableCell>
          )}

          {!isReadonly && <TableCell {...columns.actions} />}
        </TableRow>
      </TableHeader>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="model-group-droppable">
          {(
            { innerRef: droppableInnerRef, droppableProps, placeholder },
            { isUsingPlaceholder },
          ) => {
            return (
              <ModelsWrapper
                {...droppableProps}
                ref={composeRefs<HTMLDivElement>(droppableInnerRef, modelsWrapper)}
                hasModels={models.length > 0}
                isReadonly={isReadonly}
                isUsingPlaceholder={isUsingPlaceholder}
              >
                {models.length === 0 ? (
                  <TableRow mt={-1}>
                    <TableCell>
                      <ModelGroupEmpty
                        isEditable={!isReadonly}
                        onAddModel={() => {
                          if (setModelToEdit) {
                            setModelToEdit({});
                          }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {models.map((model, index) => {
                      if (!model.id) {
                        return null;
                      }

                      return (
                        <Draggable
                          key={model.id}
                          draggableId={model.id}
                          index={index}
                          isDragDisabled={!!modelGroup.externalSystem || isReadonly}
                        >
                          {(
                            { innerRef: draggableInnerRef, draggableProps, dragHandleProps },
                            { isDragging },
                          ) => (
                            <ModelRow
                              ref={draggableInnerRef}
                              isDragging={isDragging}
                              {...draggableProps}
                              py={20}
                            >
                              {!isReadonly && !modelGroup.externalSystem && (
                                <TableCell {...columns.dragHandle}>
                                  <Tooltip content="Change Order" {...tooltipProps}>
                                    <span>
                                      <DragHandle {...dragHandleProps} aria-label="Change Order" />
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              )}

                              {isRiskalyze ? (
                                <TableCell {...columns.riskBands} justifyContent="center">
                                  <img
                                    alt="Riskalyze Risk"
                                    src={`https://1dc018be4716da4aaa18-5d73971c6ce0cd6d739efc4134c4b48a.ssl.cf2.rackcdn.com/v2/standard/60/r${model.riskScore}.png`}
                                    css="width: 32px"
                                  />
                                </TableCell>
                              ) : (
                                showRiskToleranceWorkflow && (
                                  <TableCell
                                    {...columns.riskBands}
                                    justifyContent={isReadonly ? 'center' : 'flex-start'}
                                  >
                                    {isReadonly ? (
                                      <Box display="flex" alignItems="center">
                                        <Text
                                          fontWeight={500}
                                          color="neutral.600"
                                          fontSize="small"
                                          width={40}
                                          textAlign="center"
                                        >
                                          {model.riskRange.from}
                                        </Text>

                                        <Text fontWeight={500} color="neutral.400" fontSize="small">
                                          -
                                        </Text>

                                        <Text
                                          fontWeight={500}
                                          color="neutral.600"
                                          fontSize="small"
                                          width={40}
                                          textAlign="center"
                                        >
                                          {model.riskRange.to}
                                        </Text>
                                      </Box>
                                    ) : (
                                      <Box display="flex" alignItems="center">
                                        <Box width={45}>
                                          <Input
                                            value={
                                              index + 1 === models.length
                                                ? `${model.riskRange.from}+`
                                                : model.riskRange.from
                                            }
                                            style={{ textAlign: 'center' }}
                                            disabled
                                          />
                                        </Box>

                                        <Text
                                          mx="8"
                                          fontWeight={500}
                                          color="neutral.500"
                                          css={`
                                            visibility: ${index + 1 !== models.length
                                              ? 'visible'
                                              : 'hidden'};
                                          `}
                                        >
                                          -
                                        </Text>

                                        <Box
                                          width={45}
                                          css={`
                                            visibility: ${index + 1 !== models.length
                                              ? 'visible'
                                              : 'hidden'};
                                          `}
                                        >
                                          <Input
                                            value={model.riskRange.to || ''}
                                            onChange={evt => {
                                              if (modelsActions) {
                                                modelsActions.updateModelAt(index, {
                                                  ...models[index],
                                                  riskRange: {
                                                    ...models[index].riskRange,
                                                    to: Number(evt.target.value),
                                                  },
                                                });
                                              }
                                            }}
                                            onKeyDown={evt => {
                                              if (evt.key === 'Enter') {
                                                // Prevent form submission
                                                evt.preventDefault();

                                                if (modelsActions) {
                                                  modelsActions.validateRiskRangeTo(
                                                    (evt.target as HTMLInputElement).value,
                                                    index,
                                                  );
                                                }
                                              }
                                            }}
                                            onBlur={evt => {
                                              if (modelsActions) {
                                                modelsActions.validateRiskRangeTo(
                                                  evt.target.value,
                                                  index,
                                                );
                                              }
                                            }}
                                            style={{ textAlign: 'center' }}
                                            type="number"
                                          />
                                        </Box>
                                      </Box>
                                    )}
                                  </TableCell>
                                )
                              )}

                              <TableCell
                                {...columns.model}
                                css={`
                                  overflow-x: ${isOrion ? 'initial' : 'hidden'};
                                `}
                              >
                                {isOrion && !isReadonly ? (
                                  <Select
                                    value={modelsOptions?.find(
                                      ({ id }) => id === model.orionModelId,
                                    )}
                                    options={modelsOptions?.filter(
                                      ({ id }) =>
                                        !models.find(
                                          ({ orionModelId }) => orionModelId && id === orionModelId,
                                        ),
                                    )}
                                    onChange={opt => {
                                      if (opt) {
                                        if (modelsActions) {
                                          modelsActions.updateModelAt(index, {
                                            ...models[index],
                                            orionModelId: opt.id,
                                            riskScore: opt.riskScore,
                                            portfolioName: opt.name,
                                          });
                                        }
                                      }

                                      if (onModelSelection) {
                                        onModelSelection();
                                      }
                                    }}
                                    isLoading={isLoading}
                                    placeholder="Select a model..."
                                    menuPortalTarget={document.body}
                                    components={{
                                      Option,
                                      MenuList,
                                    }}
                                    getOptionValue={option => option.id.toString()}
                                    getOptionLabel={option => option.name}
                                    menuPlacement="auto"
                                    maxMenuHeight={200}
                                    css="width: 100%"
                                  />
                                ) : (
                                  <TruncatedText fontWeight={500}>
                                    {model.portfolioName}
                                  </TruncatedText>
                                )}
                              </TableCell>

                              {modelGroup.externalSystem === ExternalSystemId.Orion && (
                                <TableCell {...columns.riskScore}>
                                  <RiskScoreBadge riskScore={model.riskScore} />
                                </TableCell>
                              )}

                              {!isReadonly && (
                                <TableCell {...columns.actions}>
                                  {model.assetAllocationInputModel &&
                                    model.assetAllocationInputModel.length > 0 && (
                                      <Tooltip
                                        content={
                                          modelGroup.externalSystem
                                            ? 'View Allocation'
                                            : 'Edit Allocation'
                                        }
                                        {...tooltipProps}
                                      >
                                        <IconButton
                                          variant="subtleInactive"
                                          icon="far fa-chart-pie"
                                          onClick={() => {
                                            if (setModelToEdit) {
                                              setModelToEdit(model);
                                            }
                                          }}
                                          aria-label={
                                            modelGroup.externalSystem
                                              ? 'View Allocation'
                                              : 'Edit Allocation'
                                          }
                                          disabled={isLoading}
                                          mr="4"
                                        />
                                      </Tooltip>
                                    )}

                                  {!isRiskalyze && (
                                    <Tooltip content="Delete Model" {...tooltipProps}>
                                      <IconButton
                                        variant="subtleInactive"
                                        icon="far fa-trash-alt"
                                        onClick={() => onDeleteModel(model, index)}
                                        aria-label="Delete Model"
                                        disabled={isLoading}
                                      />
                                    </Tooltip>
                                  )}
                                </TableCell>
                              )}
                            </ModelRow>
                          )}
                        </Draggable>
                      );
                    })}
                  </>
                )}

                {placeholder}
              </ModelsWrapper>
            );
          }}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};
